import {DataSourceRecord, MultiAllocationWorksheetValueIdentifier} from "src/models/common/DataSourceRecord";
import {MDAResultsRow} from "src/components/multidimensional-allocation/hooks/useMDAResultsData";

/**
 * Groups MDA data source records by worksheetId + datasourceId and returns URL-to-records map
 * @param records - Array of DataSourceRecord to group
 * @returns Map with S3 URL as key and grouped records as value
 */
export function groupMDARecordsByWorksheetAndDatasourceId(records: DataSourceRecord[]): Map<string, DataSourceRecord[]> {
    const map = new Map<string, DataSourceRecord[]>();
    const groupMap = new Map<string, DataSourceRecord[]>();
    
    // First group by worksheetId + datasourceId
    records.forEach(record => {
        const groupKey = `${record.multiAllocationWorksheetId}-${record.multiAllocationWorksheetDatasourceId}`;
        
        if (!groupMap.has(groupKey)) {
            groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey)!.push(record);
    });
    
    // Then create URL map using any S3 URL from each group
    groupMap.forEach(groupedRecords => {
        const s3Url = groupedRecords[0].multiAllocationWorksheetDatasourceResultsFileURL!;
        map.set(s3Url, groupedRecords);
    });
    return map;
}

export interface MDACalculationResult {
    calculatedValue: number;
    selectedValues: MultiAllocationWorksheetValueIdentifier[];
    error?: string;
}

/**
 * Extracts MDA value from worksheet data using company/function combination
 * @param excelData - Parsed Excel data from S3 file
 * @param valueIdentifier - Cell identifier with companyCode and function
 * @returns MDACalculationResult with calculated value or error
 */
export function extractMDAValueFromMDAWorksheetResults(
    excelData: MDAResultsRow[],
    valueIdentifier: MultiAllocationWorksheetValueIdentifier
): MDACalculationResult {
    
    if (!excelData || excelData.length === 0) {
        return { calculatedValue: 0, selectedValues: [], error: "MDA worksheet contains no data to process" };
    }
    
    try {
        const { companyCode, function: segmentFunction } = valueIdentifier;
        
        // Get value using company code (row) and function (column)
        const companyFunctionValue = getCellValueByCompanyAndFunction(excelData, companyCode, segmentFunction);
        
        if (companyFunctionValue === null) {
            return {
                calculatedValue: 0,
                selectedValues: [],
                error: `Unable to find value for Company ${companyCode} / Function ${segmentFunction} in worksheet`
            };
        }

        return {
            calculatedValue: companyFunctionValue,
            selectedValues: [valueIdentifier]
        };
        
    } catch (error) {
        return {
            calculatedValue: 0,
            selectedValues: [],
            error: `Failed to process MDA worksheet data: ${error}`
        };
    }
}


/**
 * Get cell value using company code (first column) and segment function (header row)
 */
function getCellValueByCompanyAndFunction(excelData: MDAResultsRow[], companyCode: string, segmentFunction: string): number | null {
    if (excelData.length === 0) return null;
    
    // Find function column index from header row (assuming first row contains headers)
    const headerRow = excelData[0].row;
    const functionColumnIndex = headerRow.findIndex((header, index) => 
        index > 0 && String(header).trim() === segmentFunction.trim() // Skip first column (company column)
    );
    
    if (functionColumnIndex === -1) return null;
    
    // Find company row index (company code is in first column)
    const companyRowIndex = excelData.findIndex((row, index) => 
        index > 0 && String(row.row[0]).trim() === companyCode.trim() // Skip header row
    );
    
    if (companyRowIndex === -1) return null;
    
    const cellValue = excelData[companyRowIndex].row[functionColumnIndex];
    return typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue)) || 0;
}
